import React from 'react';
import { EmptyValue } from '@/components/ui';
import { VehicleAvailability, type Vehicle } from '../../../types/Vehicle';

type Tone = 'success' | 'danger' | 'neutral';

interface EquipmentStatusPillProps {
  label: React.ReactNode;
  tone?: Tone;
}

const TONES: Record<Tone, string> = {
  success: 'tw-bg-success/10 tw-text-success',
  danger: 'tw-bg-danger/10 tw-text-danger',
  neutral: 'tw-bg-surface-alt tw-text-content-secondary'
};
const DOTS: Record<Tone, string> = {
  success: 'tw-bg-success',
  danger: 'tw-bg-danger',
  neutral: 'tw-bg-content-muted'
};
const PILL = 'tw-inline-flex tw-items-center tw-gap-1.5 tw-whitespace-nowrap tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium';

/**
 * Soft status chip used on the Equipment list – green/red pill with a leading dot,
 * matching the prototype rather than a solid Badge fill.
 */
const EquipmentStatusPill: React.FC<EquipmentStatusPillProps> = ({ label, tone = 'neutral' }) => (
  <span className={`${PILL} ${TONES[tone]}`}>
    <span className={`tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full ${DOTS[tone]}`} aria-hidden="true" />
    {label}
  </span>
);

/** A unit's shop availability: in service, out of service, or none set (null). */
export function availabilityState(vehicle: Pick<Vehicle, 'availabilityEffective' | 'availability' | 'currentStatus'>): 'InService' | 'OutOfService' | null {
  const value = vehicle.availabilityEffective ?? vehicle.availability;
  if (value === VehicleAvailability.InService || value === 'InService') return 'InService';
  if (value === VehicleAvailability.OutOfService || value === 'OutOfService') return 'OutOfService';
  const text = (vehicle.currentStatus || '').trim().toLowerCase();
  if (text === 'in service') return 'InService';
  if (text === 'out of service') return 'OutOfService';
  return null;
}

export const AVAILABILITY_LABELS = { InService: 'In Service', OutOfService: 'Out of Service' } as const;

interface AvailabilityPillProps {
  vehicle: Pick<Vehicle, 'availabilityEffective' | 'availability' | 'currentStatus' | 'unitNumber'>;
  /** When given, the pill is a button that changes availability (Shop board, Set Availability). */
  onClick?: () => void;
}

/**
 * Availability pill, the same on the Fleet list and the Shop board (EQ-17): green in service, red out
 * of service, and one neutral grey pill with an en dash when none is set.
 */
export const AvailabilityPill: React.FC<AvailabilityPillProps> = ({ vehicle, onClick }) => {
  const state = availabilityState(vehicle);
  const tone: Tone = state === 'InService' ? 'success' : state === 'OutOfService' ? 'danger' : 'neutral';
  const label = state ? AVAILABILITY_LABELS[state] : <EmptyValue label="No availability set" />;
  if (!onClick) return <EquipmentStatusPill label={label} tone={tone} />;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Set Availability"
      aria-label={`Set availability for ${vehicle.unitNumber}: ${state ? AVAILABILITY_LABELS[state] : 'none set'}`}
      className={`${PILL} ${TONES[tone]} tw-min-h-[2rem] tw-cursor-pointer tw-border tw-border-transparent hover:tw-border-current focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2`}
    >
      <span className={`tw-h-1.5 tw-w-1.5 tw-shrink-0 tw-rounded-full ${DOTS[tone]}`} aria-hidden="true" />
      {label}
      <i className="bi bi-chevron-down tw-text-[0.65rem]" aria-hidden="true" />
    </button>
  );
};

export default EquipmentStatusPill;
